<template>
  <section>
    <header class="mb-6 space-y-4">
      <div class="flex items-center space-x-2">
          <span
              class="relative flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-white bg-hurst-dark-orange"
              aria-hidden="true">
            <template v-if="cart.hasShipping">4</template><template v-else>3</template>
          </span>
        <h2 class="text-base md:text-2xl text-hurst-light-blue font-semibold">Payment Information</h2>
      </div>
    </header>
    <div>
      <div class="rounded-md bg-red-50 p-4 w-full  mt-4" v-if="cardErrors.length > 0">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clip-rule="evenodd"/>
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm leading-5 font-medium text-red-800">
              There were errors with your submission
            </h3>
            <div class="mt-2 text-sm leading-5 text-red-700">
              <ul class="list-disc pl-5">
                <li v-for="error in cardErrors" :key="error.code">
                  {{ error.code }}: {{ error.text }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div>
        <div class="w-full">
          <form id="paymentForm" @submit.prevent="submitPayment" class="mt-4">
            <fieldset v-show="cart.amountOwedByStudent > 0">
              <template v-if="cart.amountOwedByStudent > 0">
                <div id="dropin-container"></div>
              </template>
              <div class="mt-8 pt-4 w-full">
                <div class="relative flex items-start">
                  <div class="absolute flex items-center h-5">
                    <input v-model="paymentForm.agreement"
                           id="comments"
                           type="checkbox"
                           :required="cart.amountOwedByStudent > 0"
                           class="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"/>
                  </div>
                  <div class="pl-7 text-sm leading-5">
                    <label for="comments" class="font-medium text-blue-700">I acknowledge that I have
                      read the <a
                          href="/terms-of-service"
                          target="_blank" class="underline">terms &amp; conditions and refund policy</a> for Hurst
                      Review.</label>
                  </div>
                </div>
              </div>
            </fieldset>
          </form>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import CartOrgCodeForm from './CartOrgCodeForm';
import Swal from 'sweetalert2';
import axios from 'axios';
import Bugsnag from '@bugsnag/js';

const {mapState, mapActions} = createNamespacedHelpers('cart');

export default {
  name: "CartPaymentForm",

  components: {
    CartOrgCodeForm
  },

  data() {
    return {
      braintreeToken: null,
      cardErrors: [],
      dropinInstance: null,
      errors: {},
      minimumYear: (new Date()).getFullYear(),
      paymentForm: {
        agreement: false,
        cardName: '',
        cardNumber: '',
        cardCode: '',
        cardExpMon: '',
        cardExpYr: '',
        cardZip: '',
        paymentType: 'full',
        paymentAmount: 0.00
      },
      paymentMethod: null,
      orgCode: null
    }
  },
  created() {

  },
  mounted() {
    if (this.paymentForm.paymentType === null) {
      this.paymentForm.paymentType = 'full';
    }
    axios.get('/api/braintree-token').then((response) => {
      this.braintreeToken = response.data.token;
      this.setPaymentAmount();
    })
  },

  computed: {
    ...mapState({
      cart: state => state.cart,
      user: state => state.user,
    }),

    canSubmit() {
      if (this.cart.amountOwedByStudent <= 0) {
        return true;
      }
      if (this.cart.hasShipping && this.cart.needsShipping) {
        return false;
      }
      if (this.user.needsAddress) {
        return false;
      }
      return this.paymentForm.agreement;
    },

    elevateAvailable() {
      return (
          this.cart.suggestedProducts.hasOwnProperty('elevate-rn')
          || this.cart.suggestedProducts.hasOwnProperty('elevate-pn')
      ) && !this.user.elevateAccess
    },

    elevateSku() {
      if (this.elevateAvailable && this.cart.suggestedProducts.hasOwnProperty('elevate-rn')) {
        return 'elevate-rn'
      }
      if (this.elevateAvailable && this.cart.suggestedProducts.hasOwnProperty('elevate-pn')) {
        return 'elevate-pn'
      }

      return null;
    },

    paymentType: {
      get() {
        if (this.paymentForm.paymentType === null) {
          return this.cart.paymentPlanApplied ? this.cart.paymentPlanType : 'full';
        } else {
          return this.paymentForm.paymentType;
        }
      },
      set(val) {
        this.paymentForm.paymentType = val;
      }
    },

    elevateAdded() {
      return this.cart.items.filter(el => (el.sku === 'elevate-rn' || el.sku === 'elevate-pn')).length > 0;
    }
  },


  watch: {
    'paymentForm.paymentType': async function () {
      if (this.paymentForm.paymentType === 'full' || this.paymentForm.paymentType === 'standard') {
        await this.deletePaymentPlan();
        this.setPaymentAmount();
      } else {
        await this.addPaymentPlan();
        this.setPaymentAmount();
      }
      this.setPaymentType(this.paymentForm.paymentType)
    },

    cart: function () {
      this.setPaymentAmount();
    }
  },

  methods: {
    ...mapActions(['addPaymentPlan', 'deletePaymentPlan', 'addProduct', 'deleteProduct', 'setPaymentType']),
    setupDropinUI() {
      braintree.dropin.create({
        authorization: this.braintreeToken,
        selector: '#dropin-container',
        card: {
          cardholderName: true,
          overrides: {
            fields: []
          }
        },
        applePay: {
          displayName: 'Hurst Review Services',
          paymentRequest: {
            total: {
              label: 'Hurst Review Services',
              amount: this.paymentForm.paymentAmount
            }
          },
          requiredBillingContactFields: ["postalAddress"]
        },
        googlePay: {
          googlePayVersion: 2,
          merchantId: 'BCR2DN4TR3PI7EKK',

          transactionInfo: {
            totalPriceStatus: 'FINAL',
            totalPrice: this.paymentForm.paymentAmount,
            currencyCode: 'USD',
          },
          allowedPaymentMethods: [{
            type: 'CARD',
            parameters: {
              billingAddressRequired: false,
            }
          }]
        },
        paypal: {
          'enable-funding': 'paylater',
          flow: 'checkout',
          amount: this.paymentForm.paymentAmount,
          currency: 'USD',
          buttonStyle: {
            color: 'white',
            label: 'pay',
            shape: 'rect',
            size: 'large'
          }
        },
        venmo: {}
      }).then((dropinInstance) => {
        dropinInstance.on('paymentMethodRequestable', async function (event) {
          try {
            if (event.paymentMethodIsSelected) {
              const resp = await this.dropinInstance.requestPaymentMethod();
              this.paymentMethod = resp;
            }
          } catch (e) {
            const error = e.response.data[Object.keys(e.response.data)[0]]
            if (e.response.status >= 500) {
              Bugsnag.notify(e);
            }
            this.cardErrors.push({
              code: error.code,
              text: error.message
            });
          }
        }.bind(this));
        this.dropinInstance = dropinInstance;
      });
    },
    updatePayPalAmount() {
      if (this.dropinInstance !== null) {
        this.dropinInstance.teardown();
        this.dropinInstance = null;
      }
      this.$nextTick(() => {
        this.setupDropinUI();
      });
    },
    setPaymentAmount() {
      if (this.paymentForm.paymentType === 'full') {
        this.paymentForm.paymentAmount = this.cart.amountOwedByStudent;
      } else {
        this.paymentForm.paymentAmount = this.cart.minimumDue;
      }
      this.updatePayPalAmount();
    },

    async submitPayment() {
      if (!this.canSubmit) {
        Swal.fire({
          icon: 'error',
          title: 'Please Complete all fields',
          text: 'Please complete all fields before submitting your payment',
          showConfirmButton: true
        });

        return;
      }
      this.cardErrors = [];
      Swal.fire({
        icon: 'info',
        title: 'Processing Your Registration',
        html: '<p><i class="fal fa-spinner fa-spin"></i></p><p>We are processing your registration. Give us just a minute.</p>',
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showConfirmButton: false,
      });

      if (this.cart.amountOwedByStudent > 0) {
        await this.checkoutWithPaymentData();
      } else {
        await this.checkoutWithoutPaymentData();
      }

      Swal.close();

    },

    async checkoutWithoutPaymentData() {
      try {
        const {data} = await axios.post('/Checkout', {
          paymentAmount: this.paymentForm.paymentAmount
        });
        window.location = '/Checkout/complete';
      } catch (e) {
        console.log(e.response);
        Bugsnag.notify(e);
        if (e.response.status >= 500) {
          this.cardErrors.push({code: `HTTP_${e.response.status}`, text: e.response.data.message})
        }
        Swal.close();
      }
    },
    toggleProduct(sku, suggestionId) {
      console.log(sku, suggestionId);
      Swal.fire({
        icon: 'info',
        title: 'Updating Your Cart',
        html: '<p><i class="fal fa-spinner fa-spin"></i></p>',
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showConfirmButton: false,
      });
      if (this.cart.items.filter(el => el.sku === sku).length > 0) {
        this.deleteProduct({sku, suggestionId}).then(() => {
          Swal.close()
        });
      } else {
        try {
          this.addProduct({sku, suggestionId}).then(() => {
            Swal.close()
          });
        } catch (e) {
          console.log(e);
        }
      }
    },
    async checkoutWithPaymentData() {
      try {
        if (this.paymentMethod === null) {
          try {
            const resp = await this.dropinInstance.requestPaymentMethod();
            this.paymentMethod = resp;
          } catch (e) {
            let error;
            if (e.name ?? null === 'DropinError') {
              Bugsnag.notify(e);
              error = {
                code: '0422',
                message: e.message
              }

            } else {
              error = e.response.data[Object.keys(e.response.data)[0]];
              if (e.response.status >= 500) {
                Bugsnag.notify(e);
              }
            }
            this.cardErrors.push({
              code: error.code,
              text: error.message
            });
            Swal.close();
            return;
          }
        }
        const {data} = await axios.post('/Checkout', {
          ...this.paymentMethod,
          paymentAmount: this.paymentForm.paymentAmount,
        });
        window.location = '/Checkout/complete';
      } catch (e) {
        console.log(e);
        const error = e.response.data[Object.keys(e.response.data)[0]]

        if (e.response.status >= 500) {
          Bugsnag.notify(e);
        }
        this.cardErrors.push({
          code: error.code,
          text: error.message
        });
        Swal.close();
      }
      // const authData = {
      //     clientKey: this.pubKey,
      //     apiLoginID: this.apiKey
      // };
      //
      // const cardData = {
      //     cardNumber: this.paymentForm.cardNumber.replace(/\s+/g,''),
      //     month: this.paymentForm.cardExpMon,
      //     year: this.paymentForm.cardExpYr,
      //     cardCode: this.paymentForm.cardCode,
      //     zip: this.paymentForm.cardZip,
      //     fullName: this.paymentForm.cardName
      // };
      //
      // const secureData = {
      //     authData,
      //     cardData
      // };

      // await Accept.dispatchData(secureData, async (response) => {
      //     if (response.messages.resultCode === "Error") {
      //         this.cardErrors = response.messages.message;
      //         Swal.close();
      //     } else {
      //         try {
      //             const { data } = await axios.post('/Checkout', {
      //                 opaqueData: response.opaqueData,
      //                 paymentAmount: this.paymentForm.paymentAmount
      //             });
      //             window.location = '/Checkout/complete';
      //         }catch(e) {
      //             const error = e.response.data[Object.keys(e.response.data)[0]]
      //             if(e.response.status>=500) {
      //                 Bugsnag.notify(e);
      //             }
      //             this.cardErrors.push({
      //                 code: error.code,
      //                 text: error.message
      //             });
      //             Swal.close();
      //         }
      //     }
      // });
    }
  }

}
</script>

<style scoped>

</style>